import { SectionManifest, ValidationResult, ViewDSL } from './types';
import { DezzyValidator } from './validator';
const viewSchema = require('@dezzy/schemas/view.schema.json');

export interface GovernanceRule {
  id: string;
  name: string;
  description: string;
  severity: 'error' | 'warning' | 'info';
  validate: (context: GovernanceContext) => Promise<ValidationResult[]>;
}

export interface GovernanceContext {
  sections: SectionManifest[];
  views: ViewDSL[];
  tokens: Record<string, any>;
  proposedChanges?: {
    type: 'section' | 'view' | 'token';
    action: 'create' | 'update' | 'delete';
    target: string;
    data: any;
  };
}

export interface GovernanceReport {
  passed: boolean;
  results: ValidationResult[];
  suggestions: string[];
  blockers: string[];
}

export class GovernanceEngine {
  private rules: GovernanceRule[] = [];
  private validator: DezzyValidator;

  constructor() {
    this.validator = new DezzyValidator(viewSchema, {});
    this.initializeDefaultRules();
  }

  private initializeDefaultRules() {
    // Reuse-first enforcement rule
    this.addRule({
      id: 'reuse-first',
      name: 'Reuse First Policy',
      description: 'Enforce reuse of existing sections before creating new ones',
      severity: 'error',
      validate: async (context) => {
        const results: ValidationResult[] = [];
        
        if (context.proposedChanges?.action === 'create' && context.proposedChanges.type === 'section') {
          const proposedName = context.proposedChanges.target;
          const similarSections = this.findSimilarSections(proposedName, context.sections);
          
          if (similarSections.length > 0) {
            results.push({
              valid: false,
              errors: [`Similar sections exist: ${similarSections.join(', ')}`],
              warnings: ['Consider adding a variant to an existing section instead'],
              suggestions: [
                'Review existing sections for reuse opportunities',
                'Use composition patterns when appropriate',
                'Add variants to existing sections rather than creating new ones'
              ]
            });
          }
        }
        
        return results;
      }
    });

    // Token consistency rule
    this.addRule({
      id: 'token-consistency',
      name: 'Design Token Consistency',
      description: 'Ensure consistent use of design tokens across sections',
      severity: 'error',
      validate: async (context) => {
        const results: ValidationResult[] = [];
        
        // Check if all sections use tokens from the approved token set
        for (const section of context.sections) {
          const usedTokens = this.extractTokensFromSection(section);
          const invalidTokens = usedTokens.filter(token => !context.tokens[token]);
          
          if (invalidTokens.length > 0) {
            results.push({
              valid: false,
              errors: [`Section "${section.name}" uses invalid tokens: ${invalidTokens.join(', ')}`],
              suggestions: [
                'Use only tokens defined in the design system',
                'Add missing tokens to the token registry',
                'Review token naming conventions'
              ]
            });
          }
        }
        
        return results;
      }
    });

    // Section manifest validation rule
    this.addRule({
      id: 'manifest-schema',
      name: 'Section Manifest Schema',
      description: 'Validate section manifests against required schema',
      severity: 'error',
      validate: async (context) => {
        const results: ValidationResult[] = [];
        
        for (const section of context.sections) {
          const validation = await this.validator.validateSectionManifest(section);
          if (!validation.valid) {
            results.push(validation);
          }
        }
        
        return results;
      }
    });

    // Accessibility compliance rule
    this.addRule({
      id: 'accessibility',
      name: 'Accessibility Compliance',
      description: 'Ensure sections meet accessibility requirements',
      severity: 'warning',
      validate: async (context) => {
        const results: ValidationResult[] = [];
        
        for (const section of context.sections) {
          // Check for required accessibility properties
          const requiredA11yProps = ['aria-label', 'role', 'tabIndex'];
          const missingProps = requiredA11yProps.filter(prop => 
            !section.schema?.properties?.[prop]
          );
          
          if (missingProps.length > 0) {
            results.push({
              valid: false,
              warnings: [`Section "${section.name}" missing accessibility props: ${missingProps.join(', ')}`],
              suggestions: [
                'Add aria-label for screen readers',
                'Define appropriate ARIA roles',
                'Include keyboard navigation support'
              ]
            });
          }
        }
        
        return results;
      }
    });
  }

  addRule(rule: GovernanceRule) {
    this.rules.push(rule);
  }

  removeRule(ruleId: string) {
    this.rules = this.rules.filter(rule => rule.id !== ruleId);
  }

  async evaluate(context: GovernanceContext): Promise<GovernanceReport> {
    const allResults: ValidationResult[] = [];
    const suggestions: string[] = [];
    const blockers: string[] = [];

    // Run all governance rules
    for (const rule of this.rules) {
      try {
        const results = await rule.validate(context);
        allResults.push(...results);
        
        for (const result of results) {
          if (!result.valid) {
            if (rule.severity === 'error') {
              blockers.push(...(result.errors || []));
            }
            suggestions.push(...(result.suggestions || []));
          }
        }
      } catch (error) {
        allResults.push({
          valid: false,
          errors: [`Rule "${rule.name}" failed: ${error instanceof Error ? error.message : 'Unknown error'}`]
        });
      }
    }

    return {
      passed: blockers.length === 0,
      results: allResults,
      suggestions: [...new Set(suggestions)], // Remove duplicates
      blockers: [...new Set(blockers)]
    };
  }

  // Utility methods
  private findSimilarSections(name: string, sections: SectionManifest[]): string[] {
    const similarity = 0.7; // Threshold for similarity
    return sections
      .filter(section => this.calculateSimilarity(name, section.name) > similarity)
      .map(section => section.name);
  }

  private calculateSimilarity(str1: string, str2: string): number {
    // Enhanced similarity calculation with abbreviation support
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();
    
    // Check for abbreviation matches first
    const abbreviations: Record<string, string[]> = {
      'btn': ['button'],
      'card': ['card'],
      'modal': ['modal'],
      'input': ['input', 'field'],
      'form': ['form'],
      'nav': ['navigation']
    };
    
    for (const [abbrev, full] of Object.entries(abbreviations)) {
      if ((s1 === abbrev && full.some(f => s2.includes(f))) || 
          (s2 === abbrev && full.some(f => s1.includes(f)))) {
        return 0.8; // High similarity for abbreviation matches
      }
    }
    
    // Fallback to Levenshtein distance
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  private extractTokensFromSection(section: SectionManifest): string[] {
    const tokens: string[] = [];
    
    // Extract from variants
    section.variants?.forEach(variant => {
      if (variant.tokens) {
        tokens.push(...Object.values(variant.tokens).filter(token => 
          typeof token === 'string' && token.startsWith('$')
        ) as string[]);
      }
    });
    
    // Extract from default props
    if (section.defaultProps) {
      const propTokens = this.extractTokensFromObject(section.defaultProps);
      tokens.push(...propTokens);
    }
    
    return [...new Set(tokens)]; // Remove duplicates
  }

  private extractTokensFromObject(obj: any): string[] {
    const tokens: string[] = [];
    
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string' && value.startsWith('$')) {
        tokens.push(value);
      } else if (typeof value === 'object' && value !== null) {
        tokens.push(...this.extractTokensFromObject(value));
      }
    }
    
    return tokens;
  }
}

export const defaultGovernance = new GovernanceEngine();